import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Label,
} from '@parcel/ui';
import * as React from 'react';
import { isSpecSurfaceVisible } from '../../lib/spec-config.js';
import { useRefreshSpecConversations } from '../../lib/spec-conversations.js';
import { useSpecLaunchState } from '../../lib/spec-launch.js';
import { requestSpecThreadReset } from '../../lib/spec-thread-reset.js';
import { specWebClient } from '../../lib/spec.js';
import {
  type WorkspaceSummary,
  getWorkspace,
  getWorkspaceSlugFromPath,
  listWorkspaceMembers,
} from '../../lib/workspaces.js';

const RETENTION_CHOICES = [
  { days: 30 as const, label: '30 days' },
  { days: 90 as const, label: '90 days' },
  { days: 365 as const, label: '365 days' },
  { days: null, label: 'Until deleted' },
];

function rank(days: number | null): number {
  if (days == null) return 4;
  if (days === 365) return 3;
  if (days === 90) return 2;
  return 1;
}

export function SpecPrivacySettings({ pathname }: { pathname: string }) {
  const launchState = useSpecLaunchState();
  if (!isSpecSurfaceVisible(launchState)) return null;
  const slug = getWorkspaceSlugFromPath(pathname);
  return <SpecPrivacySettingsContent key={slug ?? pathname} slug={slug} />;
}

function SpecPrivacySettingsContent({ slug }: { slug: string | null }) {
  const client = specWebClient;
  const [workspace, setWorkspace] = React.useState<WorkspaceSummary | null>(null);
  const [retentionDays, setRetentionDays] = React.useState<number | null>(null);
  const [members, setMembers] = React.useState<Array<{ id: string; name: string; role: string }>>(
    [],
  );
  const [purgeMemberId, setPurgeMemberId] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [pendingDays, setPendingDays] = React.useState<30 | 90 | 365 | null | undefined>(undefined);
  const [purgeOpen, setPurgeOpen] = React.useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = React.useState(false);
  const refreshConversations = useRefreshSpecConversations(workspace?.id);

  const canManage = workspace?.role === 'owner' || workspace?.role === 'admin';

  React.useEffect(() => {
    let active = true;
    async function load() {
      if (!slug) return;
      try {
        const nextWorkspace = await getWorkspace(slug);
        const privacy = await client.getSpecPrivacy(nextWorkspace.id);
        const listed = await listWorkspaceMembers(slug);
        if (!active) return;
        setWorkspace(nextWorkspace);
        setRetentionDays(privacy.retentionDays);
        setMembers(
          listed.members.map((member) => ({
            id: member.id,
            name: member.name,
            role: member.role,
          })),
        );
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : 'Unable to load privacy settings.');
        }
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [client, slug]);

  const applyRetention = async (days: 30 | 90 | 365 | null) => {
    if (!workspace) return;
    setError(null);
    try {
      const next = await client.setSpecRetention(workspace.id, days);
      setRetentionDays(next.retentionDays);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update retention.');
    }
  };

  const chooseRetention = (days: 30 | 90 | 365 | null) => {
    if (!canManage) return;
    if (rank(days) < rank(retentionDays)) {
      setPendingDays(days);
      return;
    }
    void applyRetention(days);
  };

  const confirmPurge = async () => {
    if (!workspace || !purgeMemberId) return;
    setPurgeOpen(false);
    setError(null);
    try {
      await client.purgeSpecMember(workspace.id, purgeMemberId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to purge member content.');
    }
  };

  const confirmDeleteAll = async () => {
    if (!workspace) return;
    setDeleteAllOpen(false);
    setError(null);
    try {
      await client.deleteAllConversations(workspace.id);
      // The runtime, if mounted, drops a deleted thread that is still open in the shell.
      requestSpecThreadReset();
      // Same cache the sidebar Chats section and the Spec home recents list read; both are
      // stale-for-30s and the sidebar is unmounted here, so nothing else will refetch them.
      await refreshConversations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete chats.');
    }
  };

  return (
    <section className="space-y-4" data-testid="spec-privacy-settings">
      <div className="space-y-1">
        <h3 className="text-lg font-medium">Conversation privacy</h3>
        <p className="text-sm text-muted-foreground">
          Choose how long Spec keeps your chats, and purge a member's private content without
          reading it.
        </p>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </div>

      <div className="space-y-4 overflow-hidden rounded-lg border p-4">
        <div className="space-y-2">
          <Label htmlFor="spec-retention">Retention</Label>
          <select
            id="spec-retention"
            className="h-9 w-full rounded-md border bg-background px-3 text-sm"
            disabled={!canManage}
            value={retentionDays == null ? 'until' : String(retentionDays)}
            onChange={(event) => {
              const value = event.target.value;
              chooseRetention(value === 'until' ? null : (Number(value) as 30 | 90 | 365));
            }}
          >
            {RETENTION_CHOICES.map((choice) => (
              <option
                key={choice.label}
                value={choice.days == null ? 'until' : String(choice.days)}
              >
                {choice.label}
              </option>
            ))}
          </select>
        </div>

        {canManage ? (
          <div className="space-y-2">
            <Label htmlFor="spec-purge-member">Admin purge</Label>
            <div className="flex gap-2">
              <select
                id="spec-purge-member"
                className="h-9 min-w-0 flex-1 rounded-md border bg-background px-3 text-sm"
                value={purgeMemberId}
                onChange={(event) => setPurgeMemberId(event.target.value)}
              >
                <option value="">Select a member</option>
                {members.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name} ({member.role})
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="destructive"
                disabled={!purgeMemberId}
                onClick={() => setPurgeOpen(true)}
              >
                Purge
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Purge removes that member's private content. It never returns chat or skill bodies.
            </p>
          </div>
        ) : null}

        <div className="space-y-2">
          <Label>Your chats</Label>
          <div>
            <Button
              type="button"
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteAllOpen(true)}
            >
              Delete all my chats
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Removes every chat you created in this workspace. It cannot be undone.
          </p>
        </div>
      </div>

      <Dialog
        open={pendingDays !== undefined}
        onOpenChange={(open) => !open && setPendingDays(undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Shorten retention?</DialogTitle>
            <DialogDescription>
              Older chats will be deleted on the next retention sweep. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                const days = pendingDays;
                setPendingDays(undefined);
                if (days !== undefined) void applyRetention(days);
              }}
            >
              Shorten retention
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purge this member's Spec content?</DialogTitle>
            <DialogDescription>
              Conversations and personal skills are queued for deletion. You will not see their
              content.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="button" variant="destructive" onClick={() => void confirmPurge()}>
              Purge member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteAllOpen} onOpenChange={setDeleteAllOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete all chats?</DialogTitle>
            <DialogDescription>
              This removes every chat you created in this workspace. It cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="button" variant="destructive" onClick={() => void confirmDeleteAll()}>
              Delete all
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
